/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../Hooks/useAuth";
import Avtar from "../../assets/img/default-image.jpg";
import useClickOutside from "../../Hooks/useClickOutside";
import { DashboardText } from "../common/Dashboard";
import { AdminIcon, AdminHeaderCloseIcon } from "../../sections/AdminDashboard/shared/adminUx";
import {
  getConfigurationRuleTestRouteContext,
  isConfigurationRuleTestPath,
} from "../../data/configurationTestSimulationConfig";
import SearchField from "../common/SearchInput/SearchField";
import { useDispatch, useSelector } from 'react-redux';
import useAdminAccess from '../../Hooks/useAdminAccess';
import { resetAdminSession } from '../../Redux/Slice/adminAccessSlice';
import { FULL_ACCESS_ACCOUNT_ID } from '../../utils/rolePermissions';
import './admin-header.css';

const AdminHeader = ({ containerClass: headerContainerClass }) => {
  const [listOpen, setListOpen] = useState(false);
  const [postingOpen, setPostingOpen] = useState(false);
  const [settings, setSettings] = useState(false);
  const [notificationsAlerts, setNotificationsAlerts] = useState(false);
  const [scrolledVersion, setScrolledVersion] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { profilePicture } = useSelector((state) => state.user);
  const { activeAccount, activeAccountId, switchAccounts, switchAccount, isOwnerView } = useAdminAccess();
  const navigate = useNavigate();

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const responsive = () => {
      setIsMobile(window.matchMedia("(max-width: 1023px)").matches);
    };
    const event = () => {
      setScrolledVersion(() => {
        if (window.scrollY <= 0) {
          return false;
        }
        return true;
      });
    };

    responsive();
    document.addEventListener("scroll", event);
    window.addEventListener("resize", responsive);
    return () => {
      document.removeEventListener("scroll", event);
      window.removeEventListener("resize", responsive);
      document.body.style.overflow = "visible";
      document.body.style.width = `auto`;
    };
  }, []);

  const location = useLocation();
  const previousPage = location.state?.from || null;
  const pathnameNorm = (location.pathname || '/').replace(/\/+$/, '') || '/';
  const isRbacAssignmentChangeRolePage = /^\/admin\/settings\/rbac\/assignment\/[^/]+\/edit$/i.test(pathnameNorm);
  const rbacCloseTarget = isRbacAssignmentChangeRolePage
    ? '/admin/settings/team-members'
    : (typeof location.state?.from === 'string' && location.state.from.startsWith('/admin/settings/team-members')) ||
        location.state?.rbacReturnTo === 'team-members'
      ? '/admin/settings/team-members'
      : '/admin/settings/rbac';
  const isCustomerDetails = location.pathname.startsWith('/admin/users/customers/') && location.pathname !== '/admin/users/customers';
  const isBusinessDetails = location.pathname.startsWith('/admin/users/businesses/') && location.pathname !== '/admin/users/businesses';
  const postSubPages = ['/admin/posts/all', '/admin/posts/standard', '/admin/posts/featured', '/admin/posts/pending', '/admin/posts/flagged'];
  const isPostDetails = location.pathname.startsWith('/admin/posts/') && 
    location.pathname !== '/admin/posts' && 
    !postSubPages.includes(location.pathname);
  const isReviewDetails =
    location.pathname.startsWith('/admin/reviews/') &&
    !['/admin/reviews/all', '/admin/reviews/pending', '/admin/reviews/flagged'].includes(location.pathname);
  const isFinanceDashboard =
    location.pathname === '/admin/finance/transactions' ||
    location.pathname === '/admin/billing/transactions';
  const isFinanceDetail =
    (location.pathname.startsWith('/admin/finance/transactions/') && location.pathname !== '/admin/finance/transactions') ||
    (location.pathname.startsWith('/admin/billing/transactions/') && location.pathname !== '/admin/billing/transactions') ||
    (location.pathname.startsWith('/admin/billing/subscriptions/') && location.pathname !== '/admin/billing/subscriptions');
  const isFinanceReports = location.pathname === '/admin/finance/reports' || location.pathname === '/admin/billing/reports';
  const isComplianceArchiveRoot = location.pathname === '/admin/verification/archive';
  const isComplianceArchiveDetail = location.pathname.startsWith('/admin/verification/archive/') && location.pathname !== '/admin/verification/archive';
  const isComplianceRulebookRoot = location.pathname === '/admin/verification/rules';
  const isComplianceRulebookDetail = location.pathname.startsWith('/admin/verification/rules/') && location.pathname !== '/admin/verification/rules';
  const isVerificationDetail =
    location.pathname.startsWith('/admin/verification/') && 
    location.pathname !== '/admin/verification/queue' && 
    !location.pathname.startsWith('/admin/verification/archive') &&
    !location.pathname.startsWith('/admin/verification/rules') &&
    !location.pathname.startsWith('/admin/verification/reports');
  const isVerificationPage =
    location.pathname === '/admin/verification/queue' || isVerificationDetail;
  const isSecurityDetail =
    location.pathname.startsWith('/admin/security/') && location.pathname !== '/admin/security';
  const isFraudRoot = location.pathname === '/admin/fraud/alerts' || location.pathname === '/admin/fraud/archive' || location.pathname === '/admin/fraud/restrictions' || location.pathname === '/admin/fraud/incidents';
  const isFraudCaseDetail = location.pathname.startsWith('/admin/fraud/alerts/') && location.pathname !== '/admin/fraud/alerts';
  const isFraudArchiveDetail = location.pathname.startsWith('/admin/fraud/archive/') && location.pathname !== '/admin/fraud/archive';
  const isRestrictionDetail = location.pathname.startsWith('/admin/fraud/restrictions/') && location.pathname !== '/admin/fraud/restrictions';
  const isFraudDetail = (location.pathname.startsWith('/admin/fraud/incidents/') && location.pathname !== '/admin/fraud/incidents') || isFraudCaseDetail || isFraudArchiveDetail || isRestrictionDetail;
  const isSecurityPage =
    location.pathname === '/admin/security' || isSecurityDetail || isFraudRoot || isFraudDetail;
  const isModerationPage = location.pathname.startsWith('/admin/moderation');
  const isModerationDetail = location.pathname.startsWith('/admin/moderation/queue/') && location.pathname !== '/admin/moderation/queue';
  const isArchiveDetail = isModerationDetail && (location.state?.readOnly === true || previousPage === '/admin/moderation/violations');
  const isWorkflowDetail = location.pathname.startsWith('/admin/moderation/workflows/') && location.pathname !== '/admin/moderation/workflows';
  const isAutoFlagDetail = location.pathname.startsWith('/admin/moderation/violations/auto-flag/');
  const isViolationDetail = location.pathname.startsWith('/admin/moderation/violations/violation/');
  const isViolationCodeDetail = location.pathname.startsWith('/admin/moderation/violations/code/');
  const isModerationRuleEditor = location.pathname.startsWith('/admin/moderation/rules/') && location.pathname !== '/admin/moderation/rules';
  const isManualCaseDetail = location.pathname.startsWith('/admin/moderation/manual/') && location.pathname !== '/admin/moderation/manual';
  const isMessagingDetail = location.pathname.startsWith('/admin/communication/all/') && location.pathname !== '/admin/communication/all';
  const isQuotesDetail =
    (location.pathname.startsWith('/admin/quotes/all/') && location.pathname !== '/admin/quotes/all') ||
    location.pathname.startsWith('/admin/quotes/group/');
  const isModerationRoot = location.pathname === '/admin/moderation/queue';
  const isViolationsRoot = location.pathname === '/admin/moderation/violations';
  const isModerationRulebook = location.pathname === '/admin/moderation/rules';
  const isModerationReportDetail = location.pathname.startsWith('/admin/moderation/reports/') && location.pathname !== '/admin/moderation/reports';
  const isAdminReports = location.pathname === '/admin/reports';
  const isAdminReportDetail =
    location.pathname.startsWith('/admin/reports/') && location.pathname !== '/admin/reports';
  const isComplianceReports = location.pathname === '/admin/verification/reports';
  const isPostsRoot =
    location.pathname === '/admin/posts' ||
    location.pathname === '/admin/posts/all' ||
    location.pathname === '/admin/posts/standard' ||
    location.pathname === '/admin/posts/featured' ||
    location.pathname === '/admin/posts/pending' ||
    location.pathname === '/admin/posts/flagged';
  const isPostsPage = isPostsRoot || isPostDetails;
  const isCustomersPage = location.pathname === '/admin/users/customers' || isCustomerDetails;
  const isBusinessesPage = location.pathname === '/admin/users/businesses' || isBusinessDetails;
  const isReviewsRoot =
    location.pathname === '/admin/reviews' ||
    location.pathname === '/admin/reviews/all' ||
    location.pathname === '/admin/reviews/pending' ||
    location.pathname === '/admin/reviews/flagged';
  const isReviewsPage = isReviewsRoot || isReviewDetails;
  const isFinancePage = isFinanceDashboard || isFinanceDetail || isFinanceReports;
  const isMessagingPage = location.pathname.startsWith('/admin/communication');
  const isQuotesAdminPage = location.pathname.startsWith('/admin/quotes');
  const isComplianceArchivePage = isComplianceArchiveRoot || isComplianceArchiveDetail;
  const isComplianceRulebookPage = isComplianceRulebookRoot || isComplianceRulebookDetail;
  const isSupportTicketsPage = location.pathname.startsWith('/admin/support/tickets');
  const isTicketDetail = location.pathname.startsWith('/admin/support/tickets/') && location.pathname !== '/admin/support/tickets';
  const isRoutingRulesRoot = location.pathname === '/admin/support/routing';
  const isRoutingRuleDetail = location.pathname.startsWith('/admin/support/routing/') && location.pathname !== '/admin/support/routing';
  const isRoutingRulesPage = isRoutingRulesRoot || isRoutingRuleDetail;
  const isKnowledgeBaseRoot = location.pathname === '/admin/support/kb';
  const isKnowledgeBaseDetail = location.pathname.startsWith('/admin/support/kb/') && location.pathname !== '/admin/support/kb';
  const isKnowledgeBasePage = isKnowledgeBaseRoot || isKnowledgeBaseDetail;
  const isFeatureToggles = location.pathname === '/admin/settings/features';
  const isUnifiedAdminSettings =
    location.pathname === '/admin/settings/general' ||
    location.pathname === '/admin/settings/platform-controls' ||
    location.pathname === '/admin/settings/security' ||
    location.pathname === '/admin/settings/alerts' ||
    location.pathname === '/admin/settings/data-requests' ||
    location.pathname === '/admin/settings/data-recovery';
  const isSecurityLog = location.pathname === '/admin/settings/security-log';
  const isTeamMembersRoot = location.pathname === '/admin/settings/team-members';
  const isTeamMemberDetail = location.pathname.startsWith('/admin/settings/team-members/') && location.pathname !== '/admin/settings/team-members';
  const isTeamMembersPage = isTeamMembersRoot || isTeamMemberDetail;
  const isRolesPermissions = location.pathname.startsWith('/admin/settings/rbac');
  const isIntegrationsConfig =
    location.pathname === '/admin/integrations' || location.pathname === '/admin/settings/integrations';
  const isSettingsHistory = location.pathname === '/admin/settings/history';
  const isBackupRecovery = location.pathname === '/admin/settings/recovery';
  const isAuditChangeLog = location.pathname === '/admin/settings/audit' || location.pathname.startsWith('/admin/settings/audit/');
  const isDataExport = location.pathname === '/admin/settings/data';
  const isNotificationLog = location.pathname === '/admin/settings/notification-log' || location.pathname.startsWith('/admin/settings/notification-log/');
  const isSystemLogs = location.pathname === '/admin/system-logs' || location.pathname === '/admin/settings/system-logs';
  const isPrivacyPolicyEdit = location.pathname === '/admin/content/legal/privacy-policy';
  const isTermsConditionsEdit = location.pathname === '/admin/content/legal/terms-conditions';
  const isBlogPublished = location.pathname === '/admin/content/blog/published';
  const isBlogDraft = location.pathname === '/admin/content/blog/draft';
  const isBlogDeleted = location.pathname === '/admin/content/blog/deleted';
  const isBlogPages = isBlogPublished || isBlogDraft || isBlogDeleted;
  const isPlatformAnalytics = location.pathname.startsWith('/admin/analytics');
  const isConfigurationValidationRoot = location.pathname === '/admin/configuration/validation';
  const isConfigurationValidationDetail = location.pathname.startsWith('/admin/configuration/validation/') && location.pathname !== '/admin/configuration/validation';
  const isConfigurationValidationPage = isConfigurationValidationRoot || isConfigurationValidationDetail;
  const isConfigurationWorkflowRoot = location.pathname === '/admin/configuration/workflow';
  const isConfigurationWorkflowDetail = location.pathname.startsWith('/admin/configuration/workflow/') && location.pathname !== '/admin/configuration/workflow';
  const isConfigurationWorkflowPage = isConfigurationWorkflowRoot || isConfigurationWorkflowDetail;
  const isTradeCategoriesRoot = location.pathname === '/admin/trade-categories';
  const isTradeCategoriesDetail =
    location.pathname.startsWith('/admin/trade-categories/') &&
    location.pathname !== '/admin/trade-categories';
  const isTradeCategoriesPage = isTradeCategoriesRoot || isTradeCategoriesDetail;
  const isKeywordsRoot = location.pathname === '/admin/keywords';
  const isKeywordsDetail = location.pathname.startsWith('/admin/keywords/') && location.pathname !== '/admin/keywords';
  const isFormInventoryRoot = location.pathname === '/admin/form-inventory';
  const isFormInventoryDetail =
    location.pathname.startsWith('/admin/form-inventory/') &&
    location.pathname !== '/admin/form-inventory';
  const isFormInventoryPage = isFormInventoryRoot || isFormInventoryDetail;
  const isVerificationsPage = location.pathname === '/admin/verifications';
  const isAppealsCasesRoot = location.pathname === '/admin/appeals';
  const isAppealDetail =
    location.pathname.startsWith('/admin/appeals/') && location.pathname !== '/admin/appeals';
  const isAppealsCasesPage = isAppealsCasesRoot || isAppealDetail;
  const isComplianceItemsSourceRoot = location.pathname === '/admin/licences-badges-trade-tickets';
  const isComplianceItemsSourceDetail =
    location.pathname.startsWith('/admin/licences-badges-trade-tickets/') &&
    location.pathname !== '/admin/licences-badges-trade-tickets';
  const isComplianceItemsSourcePage = isComplianceItemsSourceRoot || isComplianceItemsSourceDetail;
  const isConfigurationKeywordsRoot = location.pathname === '/admin/configuration/keywords';
  const isConfigurationKeywordsDetail = location.pathname.startsWith('/admin/configuration/keywords/') && location.pathname !== '/admin/configuration/keywords';
  const isKeywordsPage = isKeywordsRoot || isKeywordsDetail || isConfigurationKeywordsRoot || isConfigurationKeywordsDetail;
  const isConfigurationFormInventoryRoot = location.pathname === '/admin/configuration/form-inventory';
  const isConfigurationFormInventoryDetail =
    location.pathname.startsWith('/admin/configuration/form-inventory/') &&
    location.pathname !== '/admin/configuration/form-inventory';
  const isConfigurationFormInventoryPage =
    isConfigurationFormInventoryRoot || isConfigurationFormInventoryDetail;
  const isConfigurationVerificationsPage = location.pathname === '/admin/configuration/verifications';
  const isPricingPlansRoot = location.pathname === '/admin/pricing-plans';
  const isPricingPlansDetail = location.pathname.startsWith('/admin/pricing-plans/') && location.pathname !== '/admin/pricing-plans';
  const isPricingPlansPage = isPricingPlansRoot || isPricingPlansDetail;
  const isConfigurationPlansRoot = location.pathname === '/admin/configuration/plans';
  const isConfigurationPlansDetail = location.pathname.startsWith('/admin/configuration/plans/') && location.pathname !== '/admin/configuration/plans';
  const isConfigurationPlansPage = isConfigurationPlansRoot || isConfigurationPlansDetail;
  const isConfigurationContentControlRoot = location.pathname === '/admin/configuration/content-control';
  const isConfigurationContentControlDetail = location.pathname.startsWith('/admin/configuration/content-control/') && location.pathname !== '/admin/configuration/content-control';
  const isConfigurationContentControlPage = isConfigurationContentControlRoot || isConfigurationContentControlDetail;
  const isConfigurationFlagRoot = location.pathname === '/admin/configuration/flag';
  const isConfigurationFlagDetail =
    location.pathname.startsWith('/admin/configuration/flag/') && location.pathname !== '/admin/configuration/flag';
  const isConfigurationFlagPage = isConfigurationFlagRoot || isConfigurationFlagDetail;
  const isConfigurationPublishingRoot = location.pathname === '/admin/configuration/publishing';
  const isConfigurationPublishingDetail = location.pathname.startsWith('/admin/configuration/publishing/') && location.pathname !== '/admin/configuration/publishing';
  const isConfigurationPublishingPage = isConfigurationPublishingRoot || isConfigurationPublishingDetail;
  const isConfigurationSearchMatchingRoot = location.pathname === '/admin/configuration/search-matching';
  const isConfigurationSearchMatchingDetail =
    location.pathname.startsWith('/admin/configuration/search-matching/') &&
    location.pathname !== '/admin/configuration/search-matching';
  const isConfigurationSearchMatchingPage =
    isConfigurationSearchMatchingRoot || isConfigurationSearchMatchingDetail;
  const isConfigurationQuoteMatchingDistributionRoot =
    location.pathname === '/admin/configuration/quote-matching-distribution';
  const isConfigurationQuoteMatchingDistributionDetail =
    location.pathname.startsWith('/admin/configuration/quote-matching-distribution/') &&
    location.pathname !== '/admin/configuration/quote-matching-distribution';
  const isConfigurationQuoteMatchingDistributionPage =
    isConfigurationQuoteMatchingDistributionRoot || isConfigurationQuoteMatchingDistributionDetail;
  const isConfigurationAppealsRoot = location.pathname === '/admin/configuration/appeals';
  const isConfigurationAppealsTest = /\/admin\/configuration\/appeals\/[^/]+\/test$/.test(location.pathname);
  const isConfigurationAppealsDetail =
    location.pathname.startsWith('/admin/configuration/appeals/') &&
    location.pathname !== '/admin/configuration/appeals' &&
    !isConfigurationAppealsTest;
  const isConfigurationAppealsPage = isConfigurationAppealsRoot || isConfigurationAppealsDetail || isConfigurationAppealsTest;
  const isConfigurationPermissionsAccessRoot = location.pathname === '/admin/configuration/permissions-access';
  const isConfigurationPermissionsAccessDetail =
    location.pathname.startsWith('/admin/configuration/permissions-access/') &&
    location.pathname !== '/admin/configuration/permissions-access';
  const isConfigurationPermissionsAccessPage =
    isConfigurationPermissionsAccessRoot || isConfigurationPermissionsAccessDetail;
  const isConfigurationDocumentsRoot = location.pathname === '/admin/configuration/documents';
  const isConfigurationDocumentsDetail = location.pathname.startsWith('/admin/configuration/documents/') && location.pathname !== '/admin/configuration/documents';
  const isConfigurationDocumentsPage = isConfigurationDocumentsRoot || isConfigurationDocumentsDetail;
  const isConfigurationLimitsRoot = location.pathname === '/admin/configuration/limits';
  const isConfigurationLimitsDetail = location.pathname.startsWith('/admin/configuration/limits/') && location.pathname !== '/admin/configuration/limits';
  const isConfigurationLimitsPage = isConfigurationLimitsRoot || isConfigurationLimitsDetail;
  const isConfigurationCaseActionsRoot = location.pathname === '/admin/configuration/case-actions';
  const isConfigurationCaseActionsDetail = location.pathname.startsWith('/admin/configuration/case-actions/') && location.pathname !== '/admin/configuration/case-actions';
  const isConfigurationCaseActionsPage = isConfigurationCaseActionsRoot || isConfigurationCaseActionsDetail;
  const isConfigurationSupportTicketRoot = location.pathname === '/admin/configuration/support-ticket';
  const isConfigurationSupportTicketDetail =
    location.pathname.startsWith('/admin/configuration/support-ticket/') &&
    location.pathname !== '/admin/configuration/support-ticket';
  const isConfigurationSupportTicketPage =
    isConfigurationSupportTicketRoot || isConfigurationSupportTicketDetail;
  const isConfigurationNotificationsRoot = location.pathname === '/admin/configuration/notifications';
  const isConfigurationNotificationsDetail = location.pathname.startsWith('/admin/configuration/notifications/') && location.pathname !== '/admin/configuration/notifications';
  const isConfigurationNotificationsPage = isConfigurationNotificationsRoot || isConfigurationNotificationsDetail;
  const isConfigurationAutomationRoot = location.pathname === '/admin/configuration/automation';
  const isConfigurationAutomationDetail =
    location.pathname.startsWith('/admin/configuration/automation/') && location.pathname !== '/admin/configuration/automation';
  const isConfigurationAutomationPage = isConfigurationAutomationRoot || isConfigurationAutomationDetail;
  const isConfigurationLifecycleRoot = location.pathname === '/admin/configuration/lifecycle';
  const isConfigurationLifecycleDetail =
    location.pathname.startsWith('/admin/configuration/lifecycle/') && location.pathname !== '/admin/configuration/lifecycle';
  const isConfigurationLifecyclePage = isConfigurationLifecycleRoot || isConfigurationLifecycleDetail;
  const isConfigurationRetentionRoot = location.pathname === '/admin/configuration/retention';
  const isConfigurationRetentionDetail =
    location.pathname.startsWith('/admin/configuration/retention/') && location.pathname !== '/admin/configuration/retention';
  const isConfigurationRetentionPage = isConfigurationRetentionRoot || isConfigurationRetentionDetail;
  const isConfigurationHubPlaceholderPage = isConfigurationAutomationRoot || isConfigurationLifecycleRoot;
  const { auth, setAuth } = useAuth();
  const dispatch = useDispatch();
  const [IsDropDownOpen, setDropDown] = useState(false);
  const [switchAccountOpen, setSwitchAccountOpen] = useState(false);
  const ref = useRef(null);
  const notificationsRef = useRef(null);

  // Handle click outside for general dropdowns
  useClickOutside(ref, () => {
    setSettings(false);
    setDropDown(false);
    setSwitchAccountOpen(false);
    setListOpen(false);
    setPostingOpen(false);
  });
  
  // Handle click outside specifically for notifications dropdown
  useClickOutside(notificationsRef, () => {
    setNotificationsAlerts(false);
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "visible";
      document.body.style.width = `auto`;
    }
  }, [isOpen]);

  return (
    <>
      <div className="bg-white w-full z-40">
        <div className={`${headerContainerClass || 'tp-container'} flex items-center justify-between mx-auto h-[80px]`}>
          <div className="mr-12" onClick={() => setIsOpen(false)}>
            {location.pathname === "/admin/dashboard" && (
              <Link to="/admin/dashboard">
                <img
                  className="w-[60px] h-[25px] sm:w-[65px] sm:h-[28px] lg:w-[70px] lg:h-[30px] cursor-pointer"
                  src={require("../../assets/img/trades-poster.png")}
                  alt="trade-poster"
                  onClick={() => window.scrollTo(0, 0)}
                />
              </Link>
            )}
          </div>

          {location.pathname === "/admin/dashboard" && (
            <div>
              <SearchField placeholder={"Search"} />
            </div>
          )}

          <div>
            <div className="items-center hidden lg:flex">
              {isCustomersPage || isBusinessesPage || isPostsPage || isReviewsPage || isFinancePage || isVerificationPage || isSecurityPage || isModerationPage || isMessagingPage || isQuotesAdminPage || isComplianceArchivePage || isComplianceRulebookPage || isComplianceReports || isViolationCodeDetail || isModerationRuleEditor || isSupportTicketsPage || isTicketDetail || isRoutingRulesPage || isKnowledgeBasePage || isFeatureToggles || isUnifiedAdminSettings || isTeamMembersPage || isRolesPermissions || isIntegrationsConfig || isSettingsHistory || isBackupRecovery || isAuditChangeLog || isDataExport || isNotificationLog || isSecurityLog || isSystemLogs || isPrivacyPolicyEdit || isTermsConditionsEdit || isBlogPages || isPlatformAnalytics || isConfigurationValidationPage || isConfigurationWorkflowPage || isTradeCategoriesPage || isKeywordsPage || isFormInventoryPage || isVerificationsPage || isAppealsCasesPage || isComplianceItemsSourcePage || isPricingPlansPage || isConfigurationPlansPage || isConfigurationContentControlPage || isConfigurationFlagPage || isConfigurationPublishingPage || isConfigurationSearchMatchingPage || isConfigurationQuoteMatchingDistributionPage || isConfigurationAppealsPage || isConfigurationPermissionsAccessPage || isConfigurationDocumentsPage || isConfigurationLimitsPage || isConfigurationCaseActionsPage || isConfigurationSupportTicketPage || isConfigurationNotificationsPage || isConfigurationAutomationPage || isConfigurationLifecyclePage || isConfigurationRetentionPage || isConfigurationFormInventoryPage || isConfigurationVerificationsPage || isConfigurationHubPlaceholderPage || isAdminReports || isAdminReportDetail ? (
                <div className="flex items-center">
                  <button
                    onClick={() => {
                      if (isCustomerDetails) {
                        navigate("/admin/users/customers");
                      } else if (isBusinessDetails) {
                        navigate("/admin/users/businesses");
                      } else if (isPostDetails) {
                        navigate(previousPage || "/admin/posts/all");
                      } else if (isReviewDetails) {
                        navigate("/admin/reviews/all");
                      } else if (isFinanceDetail) {
                        navigate("/admin/billing/transactions");
                      } else if (isVerificationDetail) {
                        navigate("/admin/verification/queue");
                      } else if (isComplianceArchiveDetail) {
                        navigate("/admin/verification/archive");
                      } else if (isComplianceRulebookDetail) {
                        navigate("/admin/verification/rules");
                      } else if (isComplianceReports) {
                        navigate("/admin/dashboard");
                      } else if (isFinanceReports) {
                        navigate("/admin/dashboard");
                      } else if (isFraudArchiveDetail) {
                        navigate("/admin/fraud/archive");
                      } else if (isFraudCaseDetail) {
                        navigate("/admin/fraud/alerts");
                      } else if (isRestrictionDetail) {
                        navigate("/admin/fraud/restrictions");
                      } else if (isSecurityDetail || isFraudDetail) {
                        navigate("/admin/fraud/incidents");
                      } else if (isArchiveDetail) {
                        navigate("/admin/moderation/violations");
                      } else if (isModerationDetail) {
                        navigate(previousPage || "/admin/moderation/queue");
                      } else if (isWorkflowDetail) {
                        navigate(previousPage || "/admin/moderation/workflows");
                      } else if (isAutoFlagDetail || isViolationDetail || isViolationCodeDetail) {
                        navigate(previousPage || "/admin/moderation/violations");
                      } else if (isModerationRuleEditor) {
                        navigate(previousPage || "/admin/moderation/rules");
                      } else if (isManualCaseDetail) {
                        navigate(previousPage || "/admin/moderation/manual");
                      } else if (isAdminReportDetail) {
                        navigate("/admin/reports");
                      } else if (isModerationReportDetail) {
                        navigate("/admin/reports");
                      } else if (isMessagingDetail) {
                        navigate(previousPage || "/admin/communication/all");
                      } else if (isQuotesDetail) {
                        navigate(previousPage || "/admin/quotes/all");
                      } else if (isTicketDetail) {
                        navigate(previousPage || "/admin/support/tickets");
                      } else if (isKnowledgeBaseDetail) {
                        navigate(previousPage || "/admin/support/kb");
                      } else if (isTeamMemberDetail) {
                        navigate("/admin/settings/team-members");
                      } else if (isRolesPermissions) {
                        navigate(rbacCloseTarget);
                      } else if (isPlatformAnalytics) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationRuleTestPath(location.pathname)) {
                        const ctx = getConfigurationRuleTestRouteContext(location.pathname);
                        if (ctx?.basePath && ctx?.ruleId) {
                          navigate(`${ctx.basePath}/${ctx.ruleId}/edit`);
                        }
                      } else if (isConfigurationValidationDetail) {
                        navigate("/admin/configuration/validation");
                      } else if (isConfigurationValidationRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationWorkflowDetail) {
                        navigate("/admin/configuration/workflow");
                      } else if (isConfigurationWorkflowRoot) {
                        navigate("/admin/dashboard");
                      } else if (isTradeCategoriesDetail) {
                        navigate("/admin/trade-categories");
                      } else if (isTradeCategoriesRoot) {
                        navigate("/admin/dashboard");
                      } else if (isKeywordsDetail) {
                        navigate("/admin/keywords");
                      } else if (isConfigurationKeywordsDetail) {
                        navigate("/admin/configuration/keywords");
                      } else if (isKeywordsRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationKeywordsRoot) {
                        navigate("/admin/dashboard");
                      } else if (isFormInventoryDetail) {
                        navigate("/admin/form-inventory");
                      } else if (isFormInventoryRoot) {
                        navigate("/admin/dashboard");
                      } else if (isVerificationsPage) {
                        navigate("/admin/dashboard");
                      } else if (isAppealDetail) {
                        navigate(previousPage || "/admin/appeals");
                      } else if (isAppealsCasesRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationFormInventoryDetail) {
                        navigate("/admin/configuration/form-inventory");
                      } else if (isConfigurationFormInventoryRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationVerificationsPage) {
                        navigate("/admin/dashboard");
                      } else if (isComplianceItemsSourceDetail) {
                        navigate("/admin/licences-badges-trade-tickets");
                      } else if (isComplianceItemsSourceRoot) {
                        navigate("/admin/dashboard");
                      } else if (isPricingPlansDetail) {
                        navigate("/admin/pricing-plans");
                      } else if (isPricingPlansRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationPlansDetail) {
                        navigate("/admin/configuration/plans");
                      } else if (isConfigurationPlansRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationContentControlDetail) {
                        navigate("/admin/configuration/content-control");
                      } else if (isConfigurationContentControlRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationFlagDetail) {
                        navigate("/admin/configuration/flag");
                      } else if (isConfigurationFlagRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationPublishingDetail) {
                        navigate("/admin/configuration/publishing");
                      } else if (isConfigurationPublishingRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationSearchMatchingDetail) {
                        navigate("/admin/configuration/search-matching");
                      } else if (isConfigurationSearchMatchingRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationQuoteMatchingDistributionDetail) {
                        navigate("/admin/configuration/quote-matching-distribution");
                      } else if (isConfigurationQuoteMatchingDistributionRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationAppealsDetail) {
                        navigate("/admin/configuration/appeals");
                      } else if (isConfigurationAppealsRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationPermissionsAccessDetail) {
                        navigate("/admin/configuration/permissions-access");
                      } else if (isConfigurationPermissionsAccessRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationDocumentsDetail) {
                        navigate("/admin/configuration/documents");
                      } else if (isConfigurationDocumentsRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationLimitsDetail) {
                        navigate("/admin/configuration/limits");
                      } else if (isConfigurationLimitsRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationCaseActionsDetail) {
                        navigate("/admin/configuration/case-actions");
                      } else if (isConfigurationCaseActionsRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationSupportTicketDetail) {
                        navigate("/admin/configuration/support-ticket");
                      } else if (isConfigurationSupportTicketRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationNotificationsDetail) {
                        navigate("/admin/configuration/notifications");
                      } else if (isConfigurationNotificationsRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationAutomationDetail) {
                        navigate("/admin/configuration/automation");
                      } else if (isConfigurationAutomationRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationLifecycleDetail) {
                        navigate("/admin/configuration/lifecycle");
                      } else if (isConfigurationLifecycleRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationRetentionDetail) {
                        navigate("/admin/configuration/retention");
                      } else if (isConfigurationRetentionRoot) {
                        navigate("/admin/dashboard");
                      } else if (isConfigurationHubPlaceholderPage) {
                        navigate("/admin/dashboard");
                      } else if (isFeatureToggles || isUnifiedAdminSettings || isTeamMembersPage || isIntegrationsConfig || isSettingsHistory || isBackupRecovery || isAuditChangeLog || isDataExport || isNotificationLog || isSecurityLog || isSystemLogs) {
                        navigate("/admin/dashboard");
                      } else if (isPrivacyPolicyEdit || isTermsConditionsEdit || isBlogPages) {
                        navigate("/admin/dashboard");
                      } else if (isSupportTicketsPage) {
                        navigate("/admin/dashboard");
                      } else if (isAdminReports) {
                        navigate("/admin/dashboard");
                      } else if (isModerationPage) {
                        navigate("/admin/dashboard");
                      } else if (isMessagingPage) {
                        navigate("/admin/dashboard");
                      } else {
                        if (isPostsRoot) {
                          navigate("/admin/dashboard");
                        } else if (isReviewsPage) {
                          navigate("/admin/dashboard");
                        } else if (isFinanceDashboard) {
                          navigate("/admin/dashboard");
                        } else if (isVerificationPage) {
                          navigate("/admin/dashboard");
                        } else if (isComplianceArchivePage) {
                          navigate("/admin/dashboard");
                        } else if (isComplianceRulebookPage) {
                          navigate("/admin/dashboard");
                        } else if (isRoutingRulesPage) {
                          navigate("/admin/dashboard");
                        } else if (isKnowledgeBasePage) {
                          navigate("/admin/dashboard");
                        } else if (isSecurityPage) {
                          navigate("/admin/dashboard");
                        } else {
                          navigate("/admin/dashboard");
                        }
                      }
                    }}
                    className="flex items-center justify-center w-8 h-8 rounded-full transition-colors duration-200"
                  >
                    <AdminHeaderCloseIcon className="text-sm text-offblack cursor-pointer" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-10">
                    <ul className="flex items-center gap-5">
                      {/* Bell Icon */}
                      <li
                        className="cursor-pointer hover:bg-transparent hidden lg:block relative"
                        ref={ref}
                      >
                        <AdminIcon
                          className="cursor-pointer"
                          onClick={() =>
                            setNotificationsAlerts(
                              !notificationsAlerts
                            )
                          }
                        >
                          <i className="fi fi-rs-bell" />
                        </AdminIcon>
                        {/* Notification Badge */}
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                        {notificationsAlerts && (
                          <div 
                            ref={notificationsRef}
                            className="absolute z-10 w-[350px] origin-top-right rounded-xl bg-white text-offblack pt-5 pb-10 mt-3 -right-14 shadow-2xl"
                          >
                            <div
                              class="w-0 h-0 border-l-[10px] border-l-transparent border-b-[15px] border-b-white border-r-[10px] border-r-transparent absolute left-[275px] -mt-8"
                            ></div>
                            {/* Heading */}
                            <div className="flex items-center justify-between px-5 mb-5">
                              <DashboardText variant="main" as="p" className="font-semibold capitalize">
                                Admin Notifications
                              </DashboardText>
                            </div>

                            {/* Notifications list */}
                            <div className="border-t border-t-lightgray mx-5">
                              <div className="border-b border-b-lightgray py-3 relative">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-x-2">
                                    <img
                                      src={profilePicture || Avtar}
                                      className="w-6 h-6 rounded-full object-cover"
                                    />
                                    <DashboardText variant="main" as="p" className="capitalize">
                                      System Admin
                                    </DashboardText>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <AdminIcon className="text-primaryblue pt-1">
                                      <i className="fi fi-ss-bullet" />
                                    </AdminIcon>
                                    <DashboardText variant="metadata" className="text-mediumgray">
                                      1 minute ago
                                    </DashboardText>
                                  </div>
                                </div>
                                <DashboardText
                                  variant="supporting"
                                  as="p"
                                  className="h-[20px] overflow-hidden text-ellipsis mt-2 capitalize"
                                >
                                  Welcome to the Admin Dashboard. You have full system access.
                                </DashboardText>
                              </div>
                            </div>
                          </div>
                        )}
                      </li>
                      <li className="cursor-pointer hover:bg-transparent hidden lg:block relative">
                        <Link to={"/admin/messages"}>
                          <AdminIcon>
                            <i className="fi fi-rs-comment-alt-dots" />
                          </AdminIcon>
                        </Link>
                        {/* Message Notification Badge */}
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
                      </li>
                    </ul>
                    <div ref={ref} className="relative">
                      <div>
                        <button
                          onClick={() => setDropDown(!IsDropDownOpen)}
                          type="button"
                          className="flex max-w-xs items-center rounded-full bg-lightgray-800 text-sm"
                          id="user-menu-button"
                          aria-expanded="false"
                          aria-haspopup="true"
                        >
                          <span className="sr-only">
                            Open user menu
                          </span>
                          <img
                            className="size-10 sm:size-12 rounded-full"
                            src={activeAccount?.profilePicture || profilePicture || Avtar}
                            alt="Profile"
                          />
                        </button>
                      </div>
                      {IsDropDownOpen && (
                    <div
                      className="absolute right-0 z-30 mt-2 w-72 origin-top-right rounded-xl bg-white text-offblack px-4 py-4 shadow-lg "
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="user-menu-button"
                      tabIndex={-1}
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between py-2 hover:text-primaryblue ease-in-out text-left"
                        role="menuitem"
                        tabIndex={-1}
                        onClick={() => setSwitchAccountOpen((open) => !open)}
                      >
                        <DashboardText variant="main" as="p" className="capitalize">
                          Switch Account
                        </DashboardText>
                        <AdminIcon className="text-offblack leading-none shrink-0">
                          <i
                            className={`fi ${switchAccountOpen ? 'fi-rs-angle-up' : 'fi-rs-angle-down'}`}
                            aria-hidden
                          />
                        </AdminIcon>
                      </button>
                      {switchAccountOpen && (
                        <div className="mt-1 mb-2 border-t border-lightgray pt-2 space-y-1">
                          <button
                            type="button"
                            onClick={() => {
                              switchAccount(FULL_ACCESS_ACCOUNT_ID);
                              setSwitchAccountOpen(false);
                              setDropDown(false);
                              navigate('/admin/dashboard');
                            }}
                            className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-pastalBlueWhite ${
                              isOwnerView ? 'bg-pastalBlueWhite text-primaryblue' : 'text-offblack'
                            }`}
                          >
                            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-lightgray text-xs font-medium uppercase">
                              SS
                            </span>
                            <span className="min-w-0">
                              <DashboardText variant="main" as="p" className="truncate capitalize">
                                Saim Sahi
                              </DashboardText>
                              <DashboardText variant="metadata" className="text-mediumgray truncate block">
                                Owner
                              </DashboardText>
                            </span>
                          </button>
                          {switchAccounts.map((account) => {
                            const isActive = activeAccountId === account.accountId;
                            const initials = `${account.firstName?.[0] || ''}${account.lastName?.[0] || ''}`.trim();
                            return (
                              <button
                                key={account.accountId}
                                type="button"
                                onClick={() => {
                                  switchAccount(account.accountId);
                                  setSwitchAccountOpen(false);
                                  setDropDown(false);
                                  navigate('/admin/dashboard');
                                }}
                                className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-pastalBlueWhite ${
                                  isActive ? 'bg-pastalBlueWhite text-primaryblue' : 'text-offblack'
                                }`}
                              >
                                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-lightgray text-xs font-medium uppercase">
                                  {initials || 'TM'}
                                </span>
                                <span className="min-w-0">
                                  <DashboardText variant="main" as="p" className="truncate capitalize">
                                    {account.name}
                                  </DashboardText>
                                  <DashboardText variant="metadata" className="text-mediumgray truncate block">
                                    {account.roleLabel}
                                  </DashboardText>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <button
                        onClick={() => {
                          dispatch(resetAdminSession());
                          setAuth({
                            user: null,
                            roles: null,
                            accessToken: "",
                          });
                          localStorage.removeItem("LoginSession");
                          setDropDown(false);
                          setSwitchAccountOpen(false);
                          navigate("/admin");
                        }}
                        className="block py-2 hover:text-primaryblue ease-in-out w-full text-left border-t border-lightgray"
                        role="menuitem"
                        tabIndex={-1}
                        id="user-menu-item-1"
                      >
                        <DashboardText variant="main" as="p" className="capitalize">
                          Log Out
                        </DashboardText>
                      </button>
                    </div>
                  )}
                </div>
              </div>
                </>
              )}
            </div>
            <div>
              {isUnifiedAdminSettings ? (
                <button
                  type="button"
                  onClick={() => navigate("/admin/dashboard")}
                  className="flex items-center justify-center w-8 h-8 rounded-full text-black lg:hidden"
                  aria-label="Close"
                >
                  <AdminHeaderCloseIcon className="text-sm text-offblack" />
                </button>
              ) : (
                <button
                  onClick={toggleDrawer}
                  className="text-black lg:hidden"
                >
                  {isOpen ? (
                    <>
                      <AdminHeaderCloseIcon className="text-sm text-offblack" />
                    </>
                  ) : (
                    <>
                      <AdminIcon className="text-offblack">
                        <i className="fi fi-rs-bars-staggered" />
                      </AdminIcon>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminHeader;
